import { Alert, Card, CardHeader, Icon, Skeleton, Text } from '@ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { errorMessage } from '../../../lib/errors';
import { formatDateTime } from '../../../lib/format';
import { useWeather, type Weather } from '../api';
import { conditionIcon, formatNumber, formatTemp } from '../format';
import styles from './forage.module.css';

const weekday = new Intl.DateTimeFormat('de-DE', { weekday: 'short' });

/**
 * Weather at a location from the backend's cache (EXT-001). Without data it
 * says so instead of guessing; old data is shown with its time (EXT-004).
 */
export function WeatherCard({
  locationId,
  title,
  hideWhenUnavailable = false,
}: {
  locationId: string;
  /** Location name, e.g. on the dashboard. */
  title?: string | undefined;
  /** Leave the card out while there is no data (dashboard, §6.13). */
  hideWhenUnavailable?: boolean;
}) {
  const { t } = useTranslation();
  const weather = useWeather(locationId);
  if (weather.data?.state === 'off') return null;
  if (hideWhenUnavailable && (!weather.data || weather.data.state === 'unavailable')) return null;
  return (
    <Card aria-labelledby="weather-title">
      <CardHeader
        id="weather-title"
        title={title ? t('forage.weather.at', { name: title }) : t('forage.weather.title')}
        actions={
          title ? (
            <Link to={`/standorte/${locationId}`}>{t('forage.weather.location')}</Link>
          ) : undefined
        }
      />
      {weather.error && <Text tone="danger">{errorMessage(weather.error)}</Text>}
      {!weather.data ? (
        !weather.error && (
          <div role="status" aria-label={t('common.loading')}>
            <Skeleton lines={3} />
          </div>
        )
      ) : (
        <WeatherBody weather={weather.data} />
      )}
    </Card>
  );
}

function WeatherBody({ weather }: { weather: Weather }) {
  const { t } = useTranslation();
  if (weather.state === 'unavailable') {
    return <Text tone="muted">{t('forage.weather.unavailable')}</Text>;
  }
  const c = weather.current;
  return (
    <div className={styles.weather}>
      {weather.state === 'stale' && weather.fetched_at && (
        <Alert tone="warning" title={t('forage.weather.stale')}>
          {t('forage.weather.staleText', { date: formatDateTime(weather.fetched_at) })}
        </Alert>
      )}
      {c && (
        <div className={styles.now}>
          <Icon name={conditionIcon[c.condition]} size="xl" />
          <div>
            <span className={styles.temp}>{formatTemp(c.temperature_c)}</span>
            <Text size="sm" tone="muted">
              {t(`forage.condition.${c.condition}`)}
            </Text>
          </div>
          <dl className={styles.nowFacts}>
            {c.wind_kmh != null && (
              <>
                <dt>{t('forage.weather.wind')}</dt>
                <dd>{formatNumber(c.wind_kmh)} km/h</dd>
              </>
            )}
            {c.precipitation_mm != null && (
              <>
                <dt>{t('forage.weather.rain')}</dt>
                <dd>{formatNumber(c.precipitation_mm)} mm</dd>
              </>
            )}
          </dl>
        </div>
      )}
      {weather.days.length > 0 && (
        <ol className={styles.days} aria-label={t('forage.weather.forecast')}>
          {weather.days.map((d) => (
            <li key={d.date}>
              <span className={styles.day}>{weekday.format(new Date(`${d.date}T12:00:00`))}</span>
              <Icon
                name={conditionIcon[d.condition]}
                size="sm"
                label={t(`forage.condition.${d.condition}`)}
              />
              <span className={styles.range}>
                <strong>{formatTemp(d.temp_max_c)}</strong>
                <span>{formatTemp(d.temp_min_c)}</span>
              </span>
              <span className={styles.rain}>{formatNumber(d.precipitation_mm)} mm</span>
            </li>
          ))}
        </ol>
      )}
      <Text size="xs" tone="muted">
        {weather.fetched_at &&
          t('forage.weather.fetched', { date: formatDateTime(weather.fetched_at) })}
        {weather.attribution && ` · ${t('forage.weather.source')}: ${weather.attribution}`}
      </Text>
    </div>
  );
}
